import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useSettings } from '@/context/SettingsContext';
import PosDailyReportCard from '@/components/PosDailyReportCard';
import { ArrowLeft, BarChart3, Download, FileText, AlertTriangle } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

async function downloadBlob(url, filename) {
  const resp = await axios.get(url, { ...auth(), responseType: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(resp.data);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function CashReportsPage() {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [history, setHistory] = useState([]);
  const [statementFor, setStatementFor] = useState(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/manager-cash/reports/summary`, auth()),
        axios.get(`${API}/manager-cash/reports/settlements-history?period=${period}`, auth()),
      ]);
      setSummary(s.data);
      setHistory(h.data.history || []);
    } catch (e) { toast.error('Failed to load reports'); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try { await downloadBlob(`${API}/manager-cash/reports/export.csv`, 'manager_cash_report.csv'); }
    catch { toast.error('Export failed'); }
  };

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ manager_id: statementFor.manager_id });
      if (range.from) qs.set('date_from', range.from);
      if (range.to) qs.set('date_to', range.to);
      await downloadBlob(`${API}/manager-cash/statement.pdf?${qs}`, `cash_statement_${statementFor.manager_name}.pdf`);
      setStatementFor(null);
    } catch { toast.error('Statement download failed'); }
    finally { setDownloading(false); }
  };

  if (!summary) return <div className="p-6 text-wt-text-muted">Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="cash-reports-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => navigate('/cash')} className="border-wt-border" data-testid="cash-reports-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
            <BarChart3 className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Cash Reports</h1>
            <p className="text-wt-text-muted">Manager wallets, settlements & reconciliation</p>
          </div>
        </div>
        <Button variant="outline" className="ml-auto border-wt-border" onClick={exportCsv} data-testid="cash-report-export-btn">
          <Download className="w-4 h-4 mr-2" />Export CSV
        </Button>
      </div>

      {/* POS daily sales */}
      <PosDailyReportCard formatCurrency={formatCurrency} />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cash-report-totals">
        <div className="bg-wt-paper border border-wt-gold/40 rounded-lg p-4">
          <p className="text-xs text-wt-text-muted">Total cash with managers</p>
          <p className="text-xl font-bold text-wt-gold">{formatCurrency(summary.totals.cash_with_managers)}</p>
        </div>
        <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
          <p className="text-xs text-wt-text-muted">Pending receipts / expenses</p>
          <p className="text-xl font-semibold text-amber-400">+{formatCurrency(summary.totals.direct_receipts_pending)} / −{formatCurrency(summary.totals.expenses_pending)}</p>
        </div>
        <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
          <p className="text-xs text-wt-text-muted">Receipts: linked / unlinked orders</p>
          <p className="text-xl font-semibold text-wt-text-primary">{summary.receipts_linked_orders} / {summary.receipts_unlinked}</p>
        </div>
        <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
          <p className="text-xs text-wt-text-muted">Total shortage (settlements)</p>
          <p className={`text-xl font-semibold ${summary.total_shortage < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(Math.abs(summary.total_shortage))}</p>
        </div>
      </div>

      {/* Shopify sync counts */}
      <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
        <p className="text-sm font-medium text-wt-text-primary mb-2">Shopify Paid Sync Status</p>
        <div className="flex gap-2 flex-wrap" data-testid="shopify-sync-counts">
          {Object.entries(summary.shopify_sync_counts || {}).map(([k, v]) => (
            <Badge key={k} variant="outline" className="border-wt-border text-wt-text-primary">{k}: {v}</Badge>
          ))}
          {Object.keys(summary.shopify_sync_counts || {}).length === 0 && <span className="text-xs text-wt-text-muted">No receipts yet</span>}
        </div>
      </div>

      {/* Manager wallets */}
      <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
        <p className="text-sm font-medium text-wt-text-primary mb-3">Cash With Each Manager</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="report-wallets-table">
            <thead>
              <tr className="text-wt-text-muted border-b border-wt-border/50 text-left">
                <th className="py-2 pr-2">Manager</th><th className="pr-2 text-right">From drivers (held)</th>
                <th className="pr-2 text-right">Direct receipts</th><th className="pr-2 text-right">Expenses</th>
                <th className="pr-2 text-right">Submitted</th><th className="pr-2 text-right">Balance</th><th></th>
              </tr>
            </thead>
            <tbody>
              {summary.wallets.map((w) => (
                <tr key={w.manager_id} className="border-b border-wt-border/30 text-wt-text-primary">
                  <td className="py-2 pr-2">{w.manager_name}</td>
                  <td className="pr-2 text-right">{formatCurrency(w.cash_from_drivers_held)}</td>
                  <td className="pr-2 text-right text-emerald-400">{formatCurrency(w.direct_receipts_approved)}
                    {w.direct_receipts_pending > 0 && <span className="text-amber-400"> (+{formatCurrency(w.direct_receipts_pending)})</span>}</td>
                  <td className="pr-2 text-right text-red-400">−{formatCurrency(w.expenses_approved)}
                    {w.expenses_pending > 0 && <span className="text-amber-400"> (−{formatCurrency(w.expenses_pending)})</span>}</td>
                  <td className="pr-2 text-right text-wt-text-muted">{formatCurrency(w.submitted_to_admin)}</td>
                  <td className="pr-2 text-right font-semibold text-wt-gold">{formatCurrency(w.current_balance)}</td>
                  <td className="text-right">
                    <Button variant="outline" size="sm" className="h-7 text-xs border-wt-border"
                      onClick={() => { setStatementFor(w); setRange({ from: '', to: '' }); }}
                      data-testid={`statement-btn-${w.manager_id}`}>
                      <FileText className="w-3 h-3 mr-1" />Statement PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Settlement history */}
      <div className="bg-wt-paper border border-wt-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-wt-text-primary">Settlement History</p>
          <div className="flex gap-1">
            {['daily', 'weekly', 'monthly'].map((p) => (
              <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'}
                className={`h-7 text-xs capitalize ${period !== p ? 'border-wt-border' : ''}`}
                onClick={() => setPeriod(p)} data-testid={`period-${p}-btn`}>{p}</Button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="settlement-history-table">
            <thead>
              <tr className="text-wt-text-muted border-b border-wt-border/50 text-left">
                <th className="py-2 pr-2">Period</th><th className="pr-2">Settlements</th><th className="pr-2">Managers</th>
                <th className="pr-2 text-right">Expected</th><th className="pr-2 text-right">Actual</th>
                <th className="pr-2 text-right">Difference</th><th className="pr-2">Discrepancies</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.period} className="border-b border-wt-border/30 text-wt-text-primary">
                  <td className="py-2 pr-2 font-medium">{h.period}</td>
                  <td className="pr-2">{h.count}</td>
                  <td className="pr-2 max-w-[180px] truncate">{h.managers.join(', ')}</td>
                  <td className="pr-2 text-right">{formatCurrency(h.expected_sum)}</td>
                  <td className="pr-2 text-right">{formatCurrency(h.actual_sum)}</td>
                  <td className={`pr-2 text-right ${h.difference_sum < 0 ? 'text-red-400' : h.difference_sum > 0 ? 'text-emerald-400' : ''}`}>{formatCurrency(h.difference_sum)}</td>
                  <td className="pr-2">{h.discrepancies > 0 ? <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{h.discrepancies}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {history.length === 0 && <p className="text-center text-wt-text-muted text-sm py-6">No settlements yet</p>}
        </div>
      </div>

      {/* Statement dialog */}
      <Dialog open={!!statementFor} onOpenChange={(o) => !o && setStatementFor(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-sm">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Statement — {statementFor?.manager_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-wt-text-muted">From</Label>
              <DateInput  value={range.from} max={range.to || undefined} className="mt-1" data-testid="statement-from-input"
                onChange={(e) => setRange({ ...range, from: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-wt-text-muted">To</Label>
              <DateInput  value={range.to} min={range.from || undefined} className="mt-1" data-testid="statement-to-input"
                onChange={(e) => setRange({ ...range, to: e.target.value })} />
            </div>
          </div>
          <p className="text-[11px] text-wt-text-muted">Leave empty for the full history.</p>
          <DialogFooter>
            <Button onClick={downloadStatement} disabled={downloading} data-testid="statement-download-btn">
              <Download className="w-4 h-4 mr-2" />{downloading ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
